/**
 * Buyer order-page view token (storefront side).
 *
 * The backend signs it (backend/src/utils/orderViewToken.js) and hands it to whoever created the
 * order (checkout response, payment return URL, confirmation email). With it,
 * GET /storefront/orders/:id returns the buyer's contact details and address; without it they come
 * back redacted. Here we only check its shape and carry it around.
 *
 * Where it lives: a cookie scoped to the one order page (`Path=/orders/<id>`), so it is never in
 * the address bar (analytics $current_url, Referer, screenshots) and only that page can read it.
 *   - Checkout sets it directly before navigating to /orders/<id> (rememberOrderViewToken).
 *   - Links that carry `?t=` (email, payment return) go to /order/<id>?t=…, whose
 *     getServerSideProps sets the same cookie and redirects to the clean /orders/<id>.
 * The order page reads it (readOrderViewToken) and sends it as the X-Order-Token header.
 *
 * Not HttpOnly on purpose: the order page fetches the backend from the browser and must read it.
 * Never log the token.
 */

export const ORDER_VIEW_TOKEN_RE = /^[A-Za-z0-9_-]{1,64}$/;
export const ORDER_ID_RE = /^[0-9a-fA-F]{24}$/;
export const ORDER_VIEW_COOKIE = "ovt";
export const ORDER_TOKEN_HEADER = "X-Order-Token";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

/** The token if it is a single well-formed string, else null. */
export function sanitizeOrderViewToken(value) {
  return typeof value === "string" && ORDER_VIEW_TOKEN_RE.test(value) ? value : null;
}

export function isValidOrderId(value) {
  return typeof value === "string" && ORDER_ID_RE.test(value);
}

/** `/order/:id` (the token entry point), plus `?t=` when a well-formed token is given. */
export function orderPagePath(orderId, token, extraQuery = {}) {
  const params = new URLSearchParams();
  const t = sanitizeOrderViewToken(token);
  if (t) params.set("t", t);
  Object.entries(extraQuery).forEach(([k, v]) => {
    if (v !== undefined && v !== null) params.set(k, String(v));
  });
  const qs = params.toString();
  return `/order/${encodeURIComponent(orderId)}${qs ? `?${qs}` : ""}`;
}

/**
 * Cookie string holding the token, visible only on this order's page. `path` scopes it to another
 * per-order page instead (the custom-domain payment page, /pay/<id>).
 */
export function orderViewCookie(
  orderId,
  token,
  { secure = true, path = `/orders/${orderId}` } = {},
) {
  return [
    `${ORDER_VIEW_COOKIE}=${token}`,
    `Path=${path}`,
    `Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
    "SameSite=Lax",
    ...(secure ? ["Secure"] : []),
  ].join("; ");
}

/** Server: whether the request reached us over https (behind the proxy), for the cookie's Secure flag. */
export function isHttpsRequest(req) {
  const proto = req?.headers?.["x-forwarded-proto"];
  if (typeof proto === "string") return proto.split(",")[0].trim() === "https";
  return Boolean(req?.socket?.encrypted);
}

/** Browser: keep the token for this order (checkout success). No-op for a bad id/token. */
export function rememberOrderViewToken(orderId, token) {
  const t = sanitizeOrderViewToken(token);
  if (!t || !isValidOrderId(orderId) || typeof document === "undefined") return;
  try {
    document.cookie = orderViewCookie(orderId, t, {
      secure: window.location.protocol === "https:",
    });
  } catch {
    // Cookies blocked: the page still renders, just without the buyer's details.
  }
}

/** Token from a `Cookie` header string (or document.cookie), if well-formed. */
export function parseOrderViewCookie(cookieHeader) {
  if (typeof cookieHeader !== "string") return null;
  for (const part of cookieHeader.split(";")) {
    const idx = part.indexOf("=");
    if (idx !== -1 && part.slice(0, idx).trim() === ORDER_VIEW_COOKIE) {
      return sanitizeOrderViewToken(part.slice(idx + 1).trim());
    }
  }
  return null;
}

/** Browser: the token for the order page we are on (the cookie is path-scoped to it). */
export function readOrderViewToken() {
  if (typeof document === "undefined") return null;
  try {
    return parseOrderViewCookie(document.cookie);
  } catch {
    return null;
  }
}
